/* turtees premium layer: scroll reveals, Scroll story section, stat count-up. */
(() => {
  const root = document.documentElement;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const desktop = window.matchMedia('(min-width: 990px)');

  root.classList.add('tt-js');

  /* ---------- Section reveals ---------- */
  // Main product info, header, footer and app/overlay sections are never hidden.
  const SKIP = '[id*="__main"], .header-section, .tt-story-section, [id*="product-information"], [id*="apps"]';

  function initReveals(scope = document) {
    const main = scope.querySelector ? scope.querySelector('#MainContent') || scope : scope;
    const targets = [];

    main.querySelectorAll(':scope > .shopify-section').forEach((section, index) => {
      if (index === 0 || section.matches(SKIP)) return;
      if (section.getBoundingClientRect().top < window.innerHeight * 0.9) return; // already on screen
      section.classList.add('tt-reveal');
      targets.push(section);
    });

    document.querySelectorAll('[data-tt-reveal]').forEach((el) => {
      if (el.getBoundingClientRect().top < window.innerHeight * 0.9) return;
      el.classList.add('tt-reveal');
      targets.push(el);
    });

    if (reduceMotion || !('IntersectionObserver' in window)) {
      targets.forEach((el) => el.classList.add('is-in'));
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target;
          el.classList.add('is-in');
          io.unobserve(el);
          // Drop the transform once revealed so fixed/sticky children behave normally.
          const done = (ev) => {
            if (ev.target !== el || ev.propertyName !== 'transform') return;
            el.classList.remove('tt-reveal', 'is-in');
            el.removeEventListener('transitionend', done);
          };
          el.addEventListener('transitionend', done);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.06 }
    );

    targets.forEach((el) => io.observe(el));
  }

  /* ---------- Scroll story ---------- */
  function initStory(story) {
    if (story.dataset.ttReady) return;
    story.dataset.ttReady = 'true';

    const chapters = [...story.querySelectorAll('[data-tt-chapter]')];
    const media = [...story.querySelectorAll('[data-tt-media]')];
    const current = story.querySelector('[data-tt-current]');
    const stage = story.querySelector('.tt-story__stage');
    const track = story.querySelector('.tt-story__track');
    if (!chapters.length) return;

    let active = 0;

    const setActive = (index) => {
      if (index === active) return;
      active = index;
      chapters.forEach((c, i) => c.classList.toggle('is-active', i === index));
      media.forEach((m, i) => m.classList.toggle('is-active', i === index));
      if (current) current.textContent = String(index + 1).padStart(2, '0');
    };

    // Scroll-linked progress: which chapter sits nearest the viewport centre,
    // how far through the whole track we are, and a local 0–1 for parallax.
    let ticking = false;
    const update = () => {
      ticking = false;
      if (!desktop.matches) return;

      const mid = window.innerHeight * 0.56;
      let best = 0;
      let bestDist = Infinity;
      chapters.forEach((c, i) => {
        const r = c.getBoundingClientRect();
        const centre = r.top + r.height / 2;
        const d = Math.abs(centre - mid);
        if (d < bestDist) {
          bestDist = d;
          best = i;
        }
      });
      setActive(best);

      const tr = track.getBoundingClientRect();
      const total = tr.height - window.innerHeight * 0.5;
      const progress = Math.min(1, Math.max(0, (mid - tr.top) / Math.max(total, 1)));
      stage.style.setProperty('--tt-progress', progress.toFixed(4));

      if (!reduceMotion) {
        const r = chapters[best].getBoundingClientRect();
        const local = Math.min(1, Math.max(0, (mid - r.top) / r.height));
        stage.style.setProperty('--tt-local', local.toFixed(4));
      }
    };

    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    };

    // The theme scrolls inside .page-wrapper rather than the window, so capture
    // scroll events from any container at the document level.
    document.addEventListener('scroll', onScroll, { passive: true, capture: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();

    // Small screens: reveal each chapter card as it arrives.
    if ('IntersectionObserver' in window) {
      const seen = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (e.isIntersecting) {
              e.target.classList.add('is-seen');
              seen.unobserve(e.target);
            }
          });
        },
        { rootMargin: '0px 0px -12% 0px', threshold: 0.15 }
      );
      chapters.forEach((c) => seen.observe(c));
    } else {
      chapters.forEach((c) => c.classList.add('is-seen'));
    }

    initCounters(story);
  }

  /* ---------- Stat count-up (numbers only, e.g. "9", "400W", "6") ---------- */
  function initCounters(scope) {
    const els = [...scope.querySelectorAll('[data-tt-count]')];
    if (!els.length) return;

    const run = (el) => {
      const raw = el.dataset.ttCount;
      const match = raw.match(/^(\D*)(\d+)(.*)$/);
      if (!match || reduceMotion) return;
      const [, before, num, after] = match;
      const target = parseInt(num, 10);
      const start = performance.now();
      const dur = 1100;
      const step = (t) => {
        const p = Math.min(1, (t - start) / dur);
        const eased = 1 - Math.pow(1 - p, 3);
        el.textContent = before + Math.round(target * eased) + after;
        if (p < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    };

    if (!('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            run(e.target);
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.6 }
    );
    els.forEach((el) => io.observe(el));
  }

  const boot = () => {
    initReveals(document);
    document.querySelectorAll('[data-tt-story]').forEach(initStory);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme editor: re-initialise when a section is added or re-rendered.
  document.addEventListener('shopify:section:load', (e) => {
    e.target.querySelectorAll('[data-tt-story]').forEach(initStory);
    e.target.querySelectorAll('.tt-reveal').forEach((el) => el.classList.add('is-in'));
  });
})();
